/**
 * Connections composer: what the Enter key does. Pure helpers, no React.
 *
 * Two modes, saved per user (userPagePreferences, pageKey connectionsComposer):
 *   send     Enter sends, Shift+Enter adds a new line. The behavior before the
 *            setting existed, so it is the default.
 *   newline  Enter adds a new line, Cmd+Enter (Mac) / Ctrl+Enter (everything
 *            else) sends.
 */

export const ENTER_KEY_SEND = 'send';
export const ENTER_KEY_NEWLINE = 'newline';
export const DEFAULT_ENTER_KEY = ENTER_KEY_SEND;

// isMacPlatform
export function isMacPlatform(nav = typeof navigator !== 'undefined' ? navigator : {}) {
  const platform = (nav.userAgentData && nav.userAgentData.platform) || nav.platform || '';
  return /mac|iphone|ipad|ipod/i.test(platform);
}

// isTouchDevice
//
// A coarse primary pointer is a finger. A laptop with a touchscreen still
// reports a fine primary pointer, so it keeps the keyboard behavior.
export function isTouchDevice(win = typeof window !== 'undefined' ? window : {}) {
  return typeof win.matchMedia === 'function' && win.matchMedia('(pointer: coarse)').matches;
}

// shouldSendOnKeyDown
//
// True when this keydown should send the message. Never during IME
// composition (the Enter that confirms a candidate must not send), and never
// on a touch device, where Enter always adds a new line.
export function shouldSendOnKeyDown(event, mode, { isTouch = false } = {}) {
  if (!event || event.key !== 'Enter') {
    return false;
  }
  const native = event.nativeEvent || event;
  if (native.isComposing || event.isComposing || event.keyCode === 229) {
    return false;
  }
  if (isTouch) {
    return false;
  }
  if (mode === ENTER_KEY_NEWLINE) {
    return Boolean(event.metaKey || event.ctrlKey);
  }
  return !event.shiftKey;
}

// The key and modifier words follow the platform: Return and Cmd on a Mac
// (and iPhone / iPad), Enter and Ctrl everywhere else.
export function getEnterKeyWords(isMac) {
  return { enter: isMac ? 'Return' : 'Enter', modifier: isMac ? 'Cmd' : 'Ctrl' };
}

// getEnterKeyHeading
export function getEnterKeyHeading(isMac) {
  return `When writing a message, press ${getEnterKeyWords(isMac).enter} to:`;
}

// getEnterKeyOptions
// Each option is the main line (label) and, under it, the shortcut line.
export function getEnterKeyOptions(isMac) {
  const { enter, modifier } = getEnterKeyWords(isMac);
  return [
    {
      value: ENTER_KEY_SEND,
      label: 'Send the message',
      shortcut: `Shift+${enter} for a new line`,
    },
    {
      value: ENTER_KEY_NEWLINE,
      label: 'Start a new line',
      shortcut: `${modifier}+${enter} to send`,
    },
  ];
}

// getEnterKeyTouchNote
export function getEnterKeyTouchNote(isMac) {
  return `On phones and tablets, ${getEnterKeyWords(isMac).enter} always adds a new line.`;
}

// getEnterKeyHint
export function getEnterKeyHint(mode, { isMac = false, isTouch = false } = {}) {
  const { enter, modifier } = getEnterKeyWords(isMac);
  if (isTouch) {
    return `${enter} to add a new line`;
  }
  if (mode === ENTER_KEY_NEWLINE) {
    return `${enter} to add a new line · ${modifier}+${enter} to send`;
  }
  return `${enter} to send · Shift+${enter} for new line`;
}
